import { useEffect, useReducer, useRef } from 'react';

import { cn } from 'lib/helpers/utility';

import renderButton from '../../renderButton';
import { useSubModal } from '../lib/SubModalContext';

// Same layout and defaults as the Modal's own Footer, so sheets and modals share
// the footerActions convention (secondary + primary grouped on the right).
const FooterActionsContent = ({ footerActions, onClose }) => {
  const secondaryButton =
    footerActions?.secondary === undefined
      ? { text: 'Close', onClick: onClose }
      : footerActions?.secondary;

  return (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex items-center">{footerActions?.other}</div>
      <div className="ml-auto flex items-center gap-3">
        {renderButton({
          config: secondaryButton,
          defaultText: 'Close',
          defaultVariant: 'tertiary',
          defaultOnClick: onClose,
          defaultDataQa: 'close-modal-btn'
        })}
        {renderButton({
          config: footerActions?.primary,
          defaultText: 'Save',
          defaultVariant: 'primary',
          defaultDataQa: 'primary-modal-btn'
        })}
      </div>
    </div>
  );
};

// Re-renders in sync with the host component (via subscribe) so the sheet body and
// footer stay live without re-calling openSubModal on every render.
const SubModalRenderContent = ({ propsRef, subscribe }) => {
  const [, forceRender] = useReducer((version) => version + 1, 0);

  useEffect(() => subscribe(forceRender), [subscribe]);

  const { children, footer, footerActions, contentClassName, onClose } = propsRef.current;

  return (
    <div className="flex h-full flex-col bg-white">
      <div className={cn('flex-1 overflow-y-auto p-5', contentClassName)}>{children}</div>
      {(footer || footerActions) && (
        <div className="flex h-[56px] w-full shrink-0 items-center border-0 border-t border-solid border-neutral-100 bg-white px-4 py-3">
          {footer || <FooterActionsContent footerActions={footerActions} onClose={onClose} />}
        </div>
      )}
    </div>
  );
};

/**
 * Presents arbitrary, fully reactive content through the host Modal's SubModal.
 * The body and footer are read through a ref on every host render, so state owned
 * by the host component (formik, mutations) stays live inside the sheet.
 * Renders nothing itself — drive it with `isOpen`.
 */
const SubModalRender = ({
  isOpen = false,
  title,
  onClose,
  children,
  footer,
  footerActions,
  headerActions,
  className,
  contentClassName,
  panelClassName = '!min-h-[80%]',
  stack = false
}) => {
  const { openSubModal, closeSubModal } = useSubModal();

  const propsRef = useRef({});
  propsRef.current = { children, footer, footerActions, onClose, contentClassName };

  const listenersRef = useRef(new Set());
  const subscribeRef = useRef((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  });

  useEffect(() => {
    listenersRef.current.forEach((notify) => notify());
  });

  // Holds this sheet's stack id while open, so closing removes our own entry
  // rather than whatever sheet is currently on top.
  const openedRef = useRef(null);

  useEffect(() => {
    if (!openSubModal) return undefined;

    if (!isOpen) {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
      return undefined;
    }

    openedRef.current = openSubModal({
      title,
      footer: false,
      className: className ?? '!p-0',
      panelClassName,
      headerActions,
      stack,
      content: <SubModalRenderContent propsRef={propsRef} subscribe={subscribeRef.current} />,
      actions: { onClose: () => propsRef.current.onClose?.() }
    });

    return () => {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, title]);

  return null;
};

export default SubModalRender;
